import PolicyRequired from "enums/PolicyRequired";
import useCustomFieldRenderer from "./useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import { generateCardValidationSchema } from "validations/spendingLimits";
import {
  ADD_NEW_CARD,
  APPROVE_CARD_REQUEST,
  GET_CARD_DETAIL,
  MY_CARDS,
  REQUEST_UPDATE_CARD,
  UPDATE_CARD_NICKNAME,
} from "graphql/card";
import { useMutation, useQuery } from "@apollo/client";
import { isEmpty, isEqual } from "lodash";
import { useFormik } from "formik";
import CardStatusType from "enums/CardStatusType";
import CardType from "enums/CardType";
import CardShippingSpeed from "enums/CardShippingSpeed";
import { useEffect, useMemo, useState } from "react";
import PendingRequest from "enums/PendingRequest";
import {
  CardMeta,
  cardMetaInitialValues,
  CardPolicy,
  getCardPolicyInitialValues,
  hasAtLeastOneLimit,
} from "utils/cards/cardFormUtils";
import CardRequestType from "enums/CardRequestType";
import CardRequestStatus from "enums/CardRequestStatus";

interface useCardDataProps {
  cardId?: string;
}

export function useCardData({ cardId }: useCardDataProps) {
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    undefined,
  );
  const {
    customFieldSettings,
    customFieldsInitialValues,
    transformCustomFieldsToArray,
    transformCustomFieldsToRecord,
  } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.CARD,
    inheritFrom:
      !cardId && selectedUserId
        ? {
            type: CustomFieldParentType.USER,
            id: selectedUserId,
          }
        : undefined,
  });

  const cardPolicyValidationSchema = useMemo(
    () => generateCardValidationSchema(customFieldSettings),
    [customFieldSettings],
  );
  const cardPolicyInitialValues = useMemo(
    () => getCardPolicyInitialValues(customFieldsInitialValues),
    [customFieldsInitialValues],
  );

  const [requestNewCard, { loading: creatingCard }] = useMutation(
    ADD_NEW_CARD,
    { refetchQueries: [MY_CARDS] },
  );
  const [updateCard, { loading: updatingCard }] = useMutation(
    REQUEST_UPDATE_CARD,
    { refetchQueries: [MY_CARDS] },
  );
  const [approveCardRequest, { loading: approvingCardRequest }] =
    useMutation(APPROVE_CARD_REQUEST);
  const [renameCard, { loading: renamingCard }] =
    useMutation(UPDATE_CARD_NICKNAME);

  const {
    data: cardData,
    loading: loadingCardData,
    refetch: refetchCardData,
  } = useQuery(GET_CARD_DETAIL, {
    variables: {
      id: cardId,
    },
    fetchPolicy: "network-only",
    skip: isEmpty(cardId),
  });

  const formikCardMeta = useFormik<CardMeta>({
    initialValues: cardMetaInitialValues,
    onSubmit: async () => {},
  });

  const formikCardPolicy = useFormik<CardPolicy>({
    initialValues: cardPolicyInitialValues,
    validationSchema: cardPolicyValidationSchema,
    onSubmit: async () => {},
  });

  useEffect(() => {
    const userId = formikCardMeta.values?.user?.id;
    if (userId !== selectedUserId) {
      setSelectedUserId(userId);
    }
  }, [formikCardMeta.values?.user?.id, selectedUserId]);

  const requestReviewStatus = useMemo(() => {
    if (cardData?.card) {
      if (cardData.card?.pendingRequest?.requestType === CardRequestType.NEW) {
        return PendingRequest.NewCard;
      } else if (
        cardData.card?.pendingRequest?.requestType ===
        CardRequestType.POLICY_UPDATE
      ) {
        return PendingRequest.CardLimit;
      }
    }
    return "";
  }, [cardData]);

  const isPendingTerms = useMemo(() => {
    return (
      cardData?.card?.pendingRequest?.requestStatus ===
      CardRequestStatus.PENDING_TERMS
    );
  }, [cardData]);

  useEffect(() => {
    if (cardData?.card) {
      // ========== EDITING EXISTING CARD ==========
      let cardPolicy;
      if (cardData.card?.pendingRequest?.requestType === CardRequestType.NEW) {
        cardPolicy = cardData.card?.pendingRequest?.requestData;
      } else if (
        cardData.card?.pendingRequest?.requestType ===
        CardRequestType.POLICY_UPDATE
      ) {
        cardPolicy = cardData.card?.pendingRequest?.requestData;
      } else {
        cardPolicy = cardData.card?.activePolicy;
      }

      const formikCardPolicyValues = {
        transactionLimit: cardPolicy?.transaction_limit,
        dailyLimit: cardPolicy?.daily_limit,
        weeklyLimit: cardPolicy?.weekly_limit,
        monthlyLimit: cardPolicy?.monthly_limit,
        totalLimit: cardPolicy?.total_limit,
        allowedMerchants: cardPolicy?.merchants,
        allowedCategories: cardPolicy?.allowed_categories,
        blockedMerchants: cardPolicy?.blocked_merchants,
        blockedCategories: cardPolicy?.blocked_categories,
        expenseCategory: {
          id: cardPolicy?.expenseCategoryData?.id,
          name: cardPolicy?.expenseCategoryData?.name,
          level: cardPolicy?.expenseCategoryData?.level,
        },
        allocationType: {
          key: cardPolicy?.allocationData?.key,
          name: cardPolicy?.allocationData?.name,
        },
        allocationCode: {
          id: cardPolicy?.allocationData?.id,
          allocationCode: cardPolicy?.allocationData?.allocationCode,
        },
        cancelationTimeFrame: {
          timeFrameValue: null,
          timeFrameFrequency: null,
        },
        cancelationDate: cardPolicy?.card_cancellation_date || null,
        receiptPolicy: cardPolicy?.receipt_policy || PolicyRequired.policy,
        allocationPolicy:
          cardPolicy?.allocation_policy || PolicyRequired.policy,
        customFields: transformCustomFieldsToRecord(
          cardData?.card?.customFields || [],
          customFieldSettings,
        ),
      };

      const formikCardMetaValues = {
        id: cardData?.card?.id,
        nickname: cardData?.card?.nickname || "",
        user: cardData?.card?.user,
        issuePhysicalCard: false,
        shippingSpeed:
          cardData?.card?.shippingSpeed || CardShippingSpeed.Standard,
        sentUserNotifications: "",
        email: false,
        text: false,
      };

      formikCardMeta.resetForm({ values: formikCardMetaValues });
      formikCardPolicy.resetForm({ values: formikCardPolicyValues });
    } else if (!cardId && customFieldsInitialValues) {
      // ========== CREATING NEW CARD ==========
      const currentCustomFields = formikCardPolicy.values.customFields;

      if (!isEqual(currentCustomFields, customFieldsInitialValues)) {
        formikCardPolicy.setFieldValue(
          "customFields",
          customFieldsInitialValues,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardData, customFieldSettings, customFieldsInitialValues]);

  function buildPolicy(
    policyValues: CardPolicy,
    transformCustomFieldsToArray: (fields: any) => any,
  ) {
    return {
      merchants: policyValues.allowedMerchants?.map(({ id, name }: any) => ({
        id,
        name,
      })),
      allowed_categories: policyValues.allowedCategories?.map(
        ({ id, name }: any) => ({ id, name }),
      ),
      blocked_merchants: policyValues.blockedMerchants?.map(
        ({ id, name }: any) => ({ id, name }),
      ),
      blocked_categories: policyValues.blockedCategories?.map(
        ({ id, name }: any) => ({ id, name }),
      ),
      status: CardStatusType.ACTIVE,
      transaction_limit: parseFloat(policyValues.transactionLimit) || null,
      daily_limit: parseFloat(policyValues.dailyLimit) || null,
      weekly_limit: parseFloat(policyValues.weeklyLimit) || null,
      monthly_limit: parseFloat(policyValues.monthlyLimit) || null,
      total_limit: parseFloat(policyValues.totalLimit) || null,
      card_cancellation_date: policyValues.cancelationDate
        ? policyValues.cancelationDate.valueOf()
        : null,
      expense_category: policyValues.expenseCategory?.id || null,
      allocation_id: policyValues.allocationCode?.id || null,
      receipt_policy: policyValues.receiptPolicy || PolicyRequired.policy,
      allocation_policy: policyValues.allocationPolicy || PolicyRequired.policy,
      customFields: policyValues.customFields
        ? transformCustomFieldsToArray(policyValues.customFields)
        : {},
    };
  }

  const handleRequestNewCard = async (shippingSpeed?: CardShippingSpeed) => {
    const metaValues = formikCardMeta.values;
    const policyValues = formikCardPolicy.values;

    const policy = buildPolicy(policyValues, transformCustomFieldsToArray);

    // Construct the final variables object
    let variables = {
      input: {
        nickname: metaValues.nickname,
        userId: metaValues.user?.id,
        type: metaValues.issuePhysicalCard
          ? CardType.PHYSICAL
          : CardType.VIRTUAL,
        policy,
        issuePhysicalCard: metaValues.issuePhysicalCard,
        ...(shippingSpeed && { shippingSpeed }),
      },
    };

    try {
      const result = await requestNewCard({ variables });
      return result;
    } catch (error) {
      console.error("Error requesting new card:", error);
      throw error;
    }
  };

  const handleUpdateCard = async () => {
    const metaValues = formikCardMeta.values;
    const policyValues = formikCardPolicy.values;

    if (!metaValues.id) {
      throw new Error("Card ID is required for updating the card.");
    }

    const input = buildPolicy(policyValues, transformCustomFieldsToArray);

    let variables = {
      id: metaValues.id,
      input,
    };

    try {
      const result = await updateCard({ variables });
      return result;
    } catch (error) {
      console.error("Error updating card:", error);
      throw error;
    }
  };

  // A nickname is a label, not a spend control, so it saves directly with no approval.
  const handleRenameCard = async () => {
    const { id, nickname } = formikCardMeta.values;
    if (!id) throw new Error("Card ID is required for renaming the card.");
    return renameCard({ variables: { id, nickname: nickname ?? "" } });
  };

  const handleApproveCardRequest = async () => {
    const metaValues = formikCardMeta.values;
    const policyValues = formikCardPolicy.values;

    if (!metaValues.id || !cardData?.card?.pendingRequest?.id) {
      throw new Error("Card ID is required for updating the card.");
    }

    const input = buildPolicy(policyValues, transformCustomFieldsToArray);

    let variables = {
      id: metaValues.id,
      input,
      cardRequestId: cardData?.card?.pendingRequest?.id,
    };
    try {
      const result = await approveCardRequest({ variables });
      return result;
    } catch (error) {
      console.error("Error approving card request:", error);
      throw error;
    }
  };

  const specificLoadingStates = {
    loadingCardData,
    creatingCard,
    updatingCard,
    approvingCardRequest,
    renamingCard,
  };

  const isAnyLoading = Object.values(specificLoadingStates).some(
    (loading) => loading,
  );

  return {
    formikCardMeta,
    formikCardPolicy,
    cardData: cardData?.card,
    requestReviewStatus,
    isPendingTerms,
    specificLoadingStates,
    isAnyLoading,
    hasAtLeastOneLimit,
    handleRequestNewCard,
    handleUpdateCard,
    handleRenameCard,
    handleApproveCardRequest,
    refetchCardData,
  };
}
